import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Image,
  Divider,
  Icon,
  List,
  ListItem,
  ListIcon,
  Card,
  CardBody,
  Badge,
  SimpleGrid,
  OrderedList,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Link,
  Tooltip,
  Spinner,
} from '@chakra-ui/react';
import {
  FaCloud,
  FaSignOutAlt,
  FaBookOpen,
  FaPlay,
  FaUpload,
  FaChartLine,
  FaDownload,
  FaCog,
  FaCheckCircle,
  FaInfoCircle,
  FaRocket,
  FaLightbulb,
  FaBrain,
  FaEye,  // Add this for visualization icon
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import Footer from './Footer';
import NavDropdowns from './NavDropdowns';

import landingPageImage from '../img/landing-page.png';
import accountActivatedPageImage from '../img/account-activated.png';
import startSimulationPageImage from '../img/new-sim-1.png';
import validateCasePyFilePageImage from '../img/new-sim-2.png';
import startSimulationActivePageImage from '../img/new-sim-3.png';

import simulationResultsPageImage1 from '../img/sim-result-1.png';
import simulationResultsPageImage2 from '../img/sim-result-2.png';
import simulationResultsPageImage3 from '../img/sim-result-3.png';

import valAIRecommendationsScreenshot from '../img/val_recommendations.png';
import autoFixScreenshot from '../img/auto_fix.png';

const Tutorial = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Add eligibility state
  const [jobEligibility, setJobEligibility] = useState(null);
  const [jobEligibilityLoading, setJobEligibilityLoading] = useState(false);

  // Add useEffect to fetch eligibility when user is logged in
  useEffect(() => {
    if (user) {
      fetchJobEligibility();
    }
  }, [user]);

  // Add fetchJobEligibility function
  const fetchJobEligibility = async () => {
    try {
      setJobEligibilityLoading(true);
      const eligibility = await apiService.checkJobEligibility();
      
      if (eligibility && typeof eligibility === 'object') {
        setJobEligibility({
          is_eligible: eligibility.is_eligible || false,
          remaining_hours: eligibility.remaining_hours || 0,
          reason: eligibility.reason || 'Unknown eligibility status'
        });
      } else {
        setJobEligibility({
          is_eligible: false,
          remaining_hours: 0,
          reason: 'Invalid response format'
        });
      }
    } catch (err) {
      console.error('Failed to fetch job eligibility:', err);
      setJobEligibility({
        is_eligible: false,
        remaining_hours: 0,
        reason: 'Failed to check eligibility'
      });
    } finally {
      setJobEligibilityLoading(false);
    }
  };
  // Table of Contents items
  // Table of Contents items
  const sections = [
    { id: 'getting-started', title: 'Getting Started', icon: FaRocket },
    { id: 'account-setup', title: 'Account Setup', icon: FaCog },
    { id: 'first-simulation', title: 'Running Your First Simulation', icon: FaPlay },
    { id: 'monitoring', title: 'Monitoring Simulations', icon: FaChartLine },
    { id: 'results', title: 'Downloading Results', icon: FaDownload },
    { id: 'visualization', title: 'Results Visualization', icon: FaEye },  // Add this new line
    { id: 'best-practices', title: 'Best Practices', icon: FaLightbulb },
    { id: 'ai-features', title: 'AI Assistance Features', icon: FaBrain },
  ];
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box>
      <Helmet>
        <title>Tutorial: Run Your First GPU Cloud CFD Simulation | SRS</title>
        <meta name="description" content="Step-by-step tutorial for your first Scale-Resolving Simulation on SRS: upload a mesh, configure the PyFR solver, launch on cloud GPUs, and visualize results." />
        <link rel="canonical" href="https://srs.numericalai.net/tutorial" />
        <meta property="og:title" content="Tutorial: Run Your First GPU Cloud CFD Simulation | SRS" />
        <meta property="og:description" content="Step-by-step tutorial for your first Scale-Resolving Simulation on SRS: upload a mesh, configure the PyFR solver, launch on cloud GPUs, and visualize results." />
        <meta property="og:url" content="https://srs.numericalai.net/tutorial" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Tutorial: Run Your First GPU Cloud CFD Simulation | SRS" />
        <meta name="twitter:description" content="Step-by-step tutorial for your first Scale-Resolving Simulation on SRS: upload a mesh, configure the PyFR solver, launch on cloud GPUs, and visualize results." />
      </Helmet>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl" py={4}>
          <HStack justify="space-between">            
            <HStack 
              spacing={1} 
              cursor="pointer" 
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.8 }} 
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: "scale(0.98)" }}
            >
              <Image
                src="/simple-logo.png"
                alt="SRS Logo"
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <NavDropdowns />
              {user ? (
                <Button 
                leftIcon={<FaCloud />}
                colorScheme="brand"
                size="sm"
                variant="ghost" as={RouterLink} to="/dashboard">
                  Dashboard
                </Button>
              ) : (
                <Button variant="ghost" as={RouterLink} to="/signin">
                  Sign In
                </Button>
              )}
              {user ? (
                <Button 
                  leftIcon={<FaSignOutAlt />}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    logout();
                  }}
                >
                  Sign Out
                </Button>
              ) : (
                <Button colorScheme="brand" as={RouterLink} to="/create-user">
                  Get Started
                </Button>
              )}
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box bg="linear-gradient(135deg, #0891b2 0%, #155e75 100%)" color="white" py={12}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center">
            <Icon as={FaBookOpen} w={16} h={16} />
            <Heading as="h1" size="2xl">
              SRS Tutorial
            </Heading>
            <Text fontSize="xl" maxW="2xl" opacity={0.9}>
              A complete guide to running GPU-accelerated simulations on our platform
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="container.xl" py={12}>
        <SimpleGrid columns={{ base: 1, lg: 4 }} spacing={8}>
          {/* Sidebar - Table of Contents */}
          <Box position={{ base: 'relative', lg: 'sticky' }} top={{ lg: '100px' }} height="fit-content">
            <Card>
              <CardBody>
                <VStack align="stretch" spacing={4}>
                  <Heading size="md" color="gray.800">
                    Table of Contents
                  </Heading>
                  <Divider />
                  <VStack align="stretch" spacing={2}>
                    {sections.map((section) => (
                      <Button
                        key={section.id}
                        leftIcon={<Icon as={section.icon} />}
                        variant="ghost"
                        justifyContent="flex-start"
                        onClick={() => scrollToSection(section.id)}
                        size="sm"
                      >
                        {section.title}
                      </Button>
                    ))}
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          </Box>

          {/* Main Content Area */}
          <Box gridColumn={{ base: 'span 1', lg: 'span 3' }}>
            <VStack spacing={12} align="stretch">
              
              {/* Introduction */}
              <Box>
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Welcome to SRS!</AlertTitle>
                    <AlertDescription>
                      This tutorial will walk you through everything you need to know to run Scale-Resolving Simulations on our platform. Follow along step-by-step to become an SRS expert.
                    </AlertDescription>
                  </Box>
                </Alert>
              </Box>

              {/* Section 1: Getting Started */}
              <Box id="getting-started">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaRocket} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">1. Getting Started</Heading>
                  </HStack>
                  
                  <Text fontSize="lg" color="gray.700" lineHeight="tall">
                    SRS is a cloud-based simulation platform that leverages the high-order PyFR engine, GPU acceleration, and AI to deliver faster, more efficient Scale-Resolving Simulations of turbulent industrial flows. Before you begin, here's what you need to know:
                  </Text>

                  <Card bg="gray.50">
                    <CardBody>
                      <Heading size="md" mb={4}>Prerequisites</Heading>
                      <List spacing={3}>
                        <ListItem display="flex" alignItems="start">
                          <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                          <Text>A valid email address for account creation</Text>
                        </ListItem>
                        <ListItem display="flex" alignItems="start">
                          <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                          <Text>Basic understanding of simulation parameters, our AI will guide you!</Text>
                        </ListItem>
                        <ListItem display="flex" alignItems="start">
                          <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                          <Text>A valid credit card</Text>
                        </ListItem>
                      </List>
                    </CardBody>
                  </Card>

                </VStack>
              </Box>

              <Divider />

              {/* Section 2: Account Setup */}
              <Box id="account-setup">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaCog} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">2. Account Setup</Heading>
                  </HStack>

                  <Text fontSize="lg" color="gray.700" lineHeight="tall">
                    Setting up your SRS account is quick and straightforward. Follow these steps to get started:
                  </Text>


                  <Card>
                    <CardBody>
                      <OrderedList spacing={4} fontSize="md">
                        <ListItem>
                          <Text fontWeight="semibold" mb={2}>Create Your Account</Text>
                          <Text color="gray.600">
                            Click "Get Started" on the landing page and fill in your details. You'll receive 2 hours of free compute time which you can buy just with 1€ + VAT!
                          </Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={2}>Verify Your Email</Text>
                          <Text color="gray.600">
                            Check your inbox for a welcome email.
                          </Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={2}>Sign In</Text>
                          <Text color="gray.600">
                            Use your email and password to access your dashboard.
                          </Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={2}>Explore Your Dashboard</Text>
                          <Text color="gray.600">
                            Familiarize yourself with the interface — your compute time, storage, and quick actions are all accessible here.
                          </Text>
                        </ListItem>

                      </OrderedList>                                                                      
                    </CardBody>
                  </Card>

                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Activation Required</AlertTitle>
                      <AlertDescription>
                      Please note that compute time is activated with the €1 Starter plan. Starter credits are also included in addition to the Standard and Professional plans.
                      </AlertDescription>
                    </Box>
                  </Alert>


                  {/* Screenshot Placeholder */}
                  <Box 
                    border="2px dashed" 
                    borderColor="gray.300" 
                    borderRadius="md" 
                    p={8} 
                    bg="gray.50"
                    textAlign="center"
                  >
                    <Image src={landingPageImage} alt="Landing Page" />
                  </Box>

                  <Box 
                    border="2px dashed" 
                    borderColor="gray.300" 
                    borderRadius="md" 
                    p={8} 
                    bg="gray.50"
                    textAlign="center"
                  >
                    <Image src={accountActivatedPageImage} alt="Account Activated" />
                  </Box>


                  <Alert status="success" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Pro Tip!</AlertTitle>
                      <AlertDescription>
                        Upgrading to Premium (by purchasing any amount of credits) unlocks AI-assisted features, priority queuing, and up to 3 simultaneous jobs.
                      </AlertDescription>
                    </Box>
                  </Alert>
                </VStack>
              </Box>

              <Divider />

              {/* Section 3: First Simulation */}
              <Box id="first-simulation">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaPlay} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">3. Running Your First Simulation</Heading>
                  </HStack>

                  <Text fontSize="lg" color="gray.700" lineHeight="tall">
                    Now for the exciting part — launching your first GPU-accelerated simulation!
                  </Text>

                  <Card>
                    <CardBody>
                      <OrderedList spacing={4} fontSize="md">
                        <ListItem>
                          <Text fontWeight="semibold" mb={2}>Navigate to Start Simulation</Text>
                          <Text color="gray.600">
                            From your dashboard, click the "Start Simulation" button or use the sidebar menu.
                          </Text>
                        </ListItem>

                        <ListItem>
                          <Text fontWeight="semibold" mb={2}>Describe Your Simulation</Text>
                          <Text color="gray.600">
                            Give your simulation a name and brief descriptive so you can easily identify it later.
                          </Text>
                        </ListItem>

                        <ListItem>
                          <Text fontWeight="semibold" mb={2}>Prepare your ZIP folder</Text>
                          <Text color="gray.600">
                            Our platform takes a zip folder containing the simulation configuration file which must be exactly named "pyfr.ini", plus any mesh files your case requires (e.g. a Gmsh <code>.msh</code> mesh).
                          </Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={2}>Validate your pyfr.ini file</Text>
                          <Text color="gray.600">
                            Inorder reduce the risk of errors, you must validate your pyfr.ini file before uploading your ZIP folder.
                            Click the "Validate pyfr.ini file" button to validate your pyfr.ini file. For free users, the validation only checks the basic syntax.
                            For paid users, the validation process is more comprehensive and includes the syntax check, system check, mesh check, and AI analysis for simulation parmeter stability. See section on AI features for more details.
                          </Text>
                        </ListItem>

                        <ListItem>
                          <Text fontWeight="semibold" mb={2}>Upload Your ZIP folder</Text>
                          <Text color="gray.600">
                            Click "Choose File" to select your ZIP folder. The folder will be uploaded to your cloud storage.
                          </Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={2}>Review and Submit</Text>
                          <Text color="gray.600">
                            Double-check your settings and click "Start Simulation". Your simulation will be queued and start running on our NVIDIA A100 GPUs.
                          </Text>
                        </ListItem>
                      </OrderedList>
                    </CardBody>
                  </Card>

                  {/* Screenshot Placeholder 1 */}
                  <Box 
                    border="2px dashed" 
                    borderColor="gray.300" 
                    borderRadius="md" 
                    p={8} 
                    bg="gray.50"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Image src={startSimulationPageImage} alt="Start Simulation step 1" />
                  </Box>                  {/* Screenshot Placeholder 2 */}
                  <Box 
                    border="2px dashed" 
                    borderColor="gray.300" 
                    borderRadius="md" 
                    p={8} 
                    bg="gray.50"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Image src={validateCasePyFilePageImage} alt="Validate pyfr.ini file" />
                  </Box>
                  <Box 
                    border="2px dashed" 
                    borderColor="gray.300" 
                    borderRadius="md" 
                    p={8} 
                    bg="gray.50"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Image src={startSimulationActivePageImage} alt="start simulation button is enabled" />
                  </Box>



                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>What Happens Next?</AlertTitle>
                      <AlertDescription>
                        Your simulation enters the queue. €1 Starter users can enqueue one job at a time, while Premium users can enqueue up to 3 simultaneously. You'll see real-time progress updates in the Jobs Portal.
                      </AlertDescription>
                    </Box>
                  </Alert>
                </VStack>
              </Box>

              <Divider />

              {/* Section 4: Monitoring */}
              <Box id="monitoring">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaChartLine} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">4. Monitoring Your Simulations</Heading>
                  </HStack>

                  <Text fontSize="lg" color="gray.700" lineHeight="tall">
                    Track your simulation progress in real-time through the Jobs Portal.
                  </Text>

                  <Card>
                    <CardBody>
                      <Heading size="md" mb={4}>Understanding Job Status</Heading>
                      <VStack align="stretch" spacing={3}>
                        <HStack>
                          <Badge colorScheme="yellow" fontSize="sm">ENQUEUE</Badge>
                          <Text color="gray.600">Your job is waiting in line</Text>
                        </HStack>
                        <HStack>
                          <Badge colorScheme="purple" fontSize="sm">PREPROCESSING</Badge>
                          <Text color="gray.600">Performin system check, grid check, assigning resources and setting up the environment.</Text>
                        </HStack>
                        <HStack>
                          <Badge colorScheme="blue" fontSize="sm">RUNNING</Badge>
                          <Text color="gray.600">Simulation is actively running on GPU</Text>
                        </HStack>
                        <HStack>
                          <Badge colorScheme="green" fontSize="sm">COMPLETED</Badge>
                          <Text color="gray.600">Simulation finished successfully</Text>
                        </HStack>
                        <HStack>
                          <Badge colorScheme="red" fontSize="sm">FAILED</Badge>
                          <Text color="gray.600">An error occurred (check logs for details)</Text>
                        </HStack>
                        <HStack>
                          <Badge colorScheme="gray" fontSize="sm">CANCELLED</Badge>
                          <Text color="gray.600">Simulation was cancelled by the user</Text>
                        </HStack>
                      </VStack>
                      
                    </CardBody>
                  </Card>

                  <Card>
                    <CardBody>
                      <Heading size="md" mb={4}>Key Metrics to Watch</Heading>
                      <List spacing={3}>
                        <ListItem display="flex" alignItems="start">
                          <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                          <Box>
                            <Text fontWeight="semibold">Estimated Time Remaining (ETA)</Text>
                            <Text color="gray.600" fontSize="sm">Predicted completion time based on current progress</Text>
                          </Box>
                        </ListItem>
                        <ListItem display="flex" alignItems="start">
                          <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                          <Box>
                            <Text fontWeight="semibold">Duration</Text>
                            <Text color="gray.600" fontSize="sm">Total time which includes all the stages from enqueue to completion and processing time which excludes the enqueue time. Hours consumped as calculed from processing time only.</Text>
                          </Box>
                        </ListItem>
                        <ListItem display="flex" alignItems="start">
                          <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                          <Box>
                            <Text fontWeight="semibold">File Size</Text>
                            <Text color="gray.600" fontSize="sm">Size of the resulting files in ZIP format</Text>
                          </Box>
                        </ListItem>
                        <ListItem display="flex" alignItems="start">
                          <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                          <Box>
                            <Text fontWeight="semibold">Action Buttons</Text>
                            <Text color="gray.600" fontSize="sm">View, download, cancel, or delete the job. Logs are available for viewing.</Text>
                          </Box>
                        </ListItem>
                        <ListItem display="flex" alignItems="start">
                        <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                          <Box>
                            <Text fontWeight="semibold">Total Consumption</Text>
                            <Text color="gray.600" fontSize="sm">Total and reaming consumed hours are shown via the progress bar.</Text>
                          </Box>
                        </ListItem>


                      </List>
                    </CardBody>
                  </Card>

                  {/* Screenshot Placeholder */}
                  <Box 
                    border="2px dashed" 
                    borderColor="gray.300" 
                    borderRadius="md" 
                    p={8} 
                    bg="gray.50"
                    textAlign="center"
                  >
                    <Image src={simulationResultsPageImage1} alt="Jobs Portal" />
                  </Box>

                  <Box 
                    border="2px dashed" 
                    borderColor="gray.300" 
                    borderRadius="md" 
                    p={8} 
                    bg="gray.50"
                    textAlign="center"
                  >
                    <Image src={simulationResultsPageImage2} alt="Jobs Portal" />
                  </Box>

                  <Box 
                    border="2px dashed" 
                    borderColor="gray.300" 
                    borderRadius="md" 
                    p={8} 
                    bg="gray.50"
                    textAlign="center"
                  >
                    <Image src={simulationResultsPageImage3} alt="Jobs Portal" />
                  </Box>

                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Need to Stop a Job?</AlertTitle>
                      <AlertDescription>
                        You can cancel a running simulation at any time from the Jobs Portal. Note that compute time already used will be deducted from your balance. Deleting a job will not deduct the compute time.
                      </AlertDescription>
                    </Box>
                  </Alert>
                </VStack>
              </Box>

              <Divider />

              {/* Section 5: Downloading Results */}
              <Box id="results">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaDownload} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">5. Downloading Your Results</Heading>
                  </HStack>

                  <Text fontSize="lg" color="gray.700" lineHeight="tall">
                    Once your simulation completes, you can download the results for analysis.
                  </Text>

                  <Card>
                    <CardBody>
                      <OrderedList spacing={4} fontSize="md">
                        <ListItem>
                          <Text fontWeight="semibold" mb={2}>Go to Jobs Portal</Text>
                          <Text color="gray.600">
                            Navigate to the Jobs section from your dashboard.
                          </Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={2}>Find Your Completed Job</Text>
                          <Text color="gray.600">
                            Look for jobs with the "COMPLETED" status badge.
                          </Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={2}>View Job Details</Text>
                          <Text color="gray.600">
                            Click on the job to expand details and see output files.
                          </Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={2}>Download Results</Text>
                          <Text color="gray.600">
                            Click the "Download" button to save result files to your computer. You can also view logs directly in the browser.
                          </Text>
                        </ListItem>
                      </OrderedList>
                    </CardBody>
                  </Card>

                  <Card bg="blue.50">
                    <CardBody>
                      <Heading size="sm" mb={3} color="blue.800">What You'll Get</Heading>
                      <List spacing={2}>
                        <ListItem display="flex" alignItems="center">
                          <ListIcon as={FaCheckCircle} color="blue.500" />
                          <Text fontSize="sm">Solver output files (PyFR .pyfrm data + VTK/PVTU exports)</Text>
                        </ListItem>
                        <ListItem display="flex" alignItems="center">
                          <ListIcon as={FaCheckCircle} color="blue.500" />
                          <Text fontSize="sm">SRS solver logs with detailed runtime information</Text>
                        </ListItem>
                        <ListItem display="flex" alignItems="center">
                          <ListIcon as={FaCheckCircle} color="blue.500" />
                          <Text fontSize="sm">Performance metrics and timing data</Text>
                        </ListItem>
                      </List>
                    </CardBody>
                  </Card>
                </VStack>
              </Box>

              <Divider />

              {/* Section 6: Results Visualization */}
              <Box id="visualization">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaEye} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">6. Results Visualization</Heading>
                  </HStack>

                  <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  The PyFR engine writes results in its native <code>.pyfrm</code> format and can export to VTK/PVTU for use with third‑party visualization tools such as <Link href="https://visit-dav.github.io/visit-website/" isExternal color="brand.500" fontWeight="bold">VisIt</Link> (developed by Lawrence Livermore National Laboratory, LLNL) and <Link href="https://www.paraview.org/" isExternal color="brand.500" fontWeight="bold">ParaView</Link> (developed by Kitware, Inc.) to inspect and analyze your SRS output. Configure the <code>[output]</code> section of your <code>pyfr.ini</code> with <code>format = pyfr, vtk</code> to write both formats simultaneously.
                  </Text>

                  {/* VisIt Section */}
                  <Card>
                    <CardBody>
                      <Heading size="lg" mb={4} color="blue.600">Visualizing SRS Results in VisIt</Heading>
                      <Text color="gray.700" mb={4}>
                        Viewing SRS simulation results in VisIt involves opening the VTK/PVTU files exported by PyFR, then using VisIt's plotting and analysis tools. Below is a concise guide to get you started.
                      </Text>

                      <Heading size="md" mb={3} color="gray.800">What you need to do</Heading>
                      <List spacing={3} mb={6}>
                        <ListItem display="flex" alignItems="start">
                          <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                          <Text>Install a compatible VisIt version for your operating system (VisIt supports multiple platforms and data formats).</Text>
                        </ListItem>
                        <ListItem display="flex" alignItems="start">
                          <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                          <Text>Confirm that your SRS output is exported in a VisIt-readable format. PyFR's VTK/PVTU export is the most reliable option; XDMF is also supported. If unsure, check the PyFR documentation for export options.</Text>
                        </ListItem>
                        <ListItem display="flex" alignItems="start">
                          <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                          <Text>Prepare your data path or database: point VisIt to the directory containing the VTK/PVTU files. VisIt can also aggregate multiple time steps if you open the group as a database.</Text>
                        </ListItem>
                      </List>

                      <Heading size="md" mb={3} color="gray.800">Core steps in VisIt</Heading>
                      <OrderedList spacing={3} mb={6}>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Start VisIt and open a database</Text>
                          <Text color="gray.600" fontSize="sm">Go to File → Open File (or Open Database) and select your PyFR-exported VTK/PVTU file or directory.</Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Create a plot</Text>
                          <Text color="gray.600" fontSize="sm">Choose an appropriate plot type (e.g., Pseudocolor for scalar fields like pressure or vorticity magnitude, Vector for velocities).</Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Apply data operations</Text>
                          <Text color="gray.600" fontSize="sm">Use Expressions to compute derived quantities (e.g., Q-criterion, λ2, or custom scalars for turbulence visualization).</Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Time and animation</Text>
                          <Text color="gray.600" fontSize="sm">If your results contain time steps, use the Animation controls to explore temporal evolution of turbulent structures.</Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Save visuals</Text>
                          <Text color="gray.600" fontSize="sm">Export images or animations via File → Save Window or Save Animation.</Text>
                        </ListItem>
                      </OrderedList>

                      <Alert status="info" borderRadius="sm">
                        <AlertIcon />
                        <Box>
                          <AlertTitle>Tips for VisIt</AlertTitle>
                          <AlertDescription fontSize="sm">
                              If your data does not appear, verify that you opened the correct database type and confirm format compatibility. For large HPC datasets, use VisIt's parallel rendering and data processing features for efficient handling. Refer to VisIt tutorials for example pipelines (loading databases, creating plots, applying operators, exporting results).
                          </AlertDescription>
                        </Box>
                      </Alert>
                    </CardBody>
                  </Card>

                  {/* ParaView Section */}
                  <Card>
                    <CardBody>
                      <Heading size="lg" mb={4} color="green.600">Visualizing SRS Results in ParaView</Heading>
                      <Text color="gray.700" mb={4}>
                        ParaView is another widely used post-processor for PyFR-exported VTK/PVTU outputs. The workflow is similar to VisIt but uses its own UI and readers.
                      </Text>

                      <Heading size="md" mb={3} color="gray.800">Steps to open SRS results in ParaView</Heading>
                      <OrderedList spacing={3} mb={6}>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Install a compatible ParaView version</Text>
                          <Text color="gray.600" fontSize="sm">Download a version appropriate for your OS. Ensure that the release includes the VTK readers you need for HPC data formats.</Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Confirm the SRS output format</Text>
                          <Text color="gray.600" fontSize="sm">PyFR's VTK/PVTU export is the most ParaView-friendly option. Set <code>format = vtk</code> in the <code>[output]</code> section of your <code>pyfr.ini</code> if you only need ParaView compatibility.</Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Load the data in ParaView</Text>
                          <Text color="gray.600" fontSize="sm">
                            Go to File → Open, then select the PyFR-exported VTK/PVTU file or directory. ParaView will load the dataset and display available fields. For multi-step outputs, open the group as a time-varying dataset rather than individual time-step files.
                          </Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Visualizing fields</Text>
                          <Text color="gray.600" fontSize="sm">Use the Properties panel to choose a representation (e.g., Volume, Surface, Glyphs). Adjust color maps and opacity to highlight important variables (pressure, velocity magnitude, vorticity).</Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Time steps and animation</Text>
                          <Text color="gray.600" fontSize="sm">If the dataset includes multiple time steps, use ParaView's animation controls to play or scrub through time. ParaView automatically exposes time information for temporal datasets.</Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Derived quantities</Text>
                          <Text color="gray.600" fontSize="sm">Use the Calculator or Expressions to compute derived fields (Q-criterion, λ2, vorticity, etc.), then visualize them alongside primary fields.</Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Saving and exporting results</Text>
                          <Text color="gray.600" fontSize="sm">Use File → Save Screenshot or Save Animation to export images or movies. You can also save a ParaView state file to reproduce your visualization setup.</Text>
                        </ListItem>
                      </OrderedList>

                      <Alert status="warning" borderRadius="sm">
                        <AlertIcon />
                          <Box>
                            <AlertTitle>Troubleshooting</AlertTitle>
                            <AlertDescription fontSize="sm">
                            If data does not load, confirm format compatibility. If needed, re-run your case with <code>format = vtk</code> in the <code>[output]</code> section of your <code>pyfr.ini</code> to produce ParaView-readable files. Review the PyFR documentation for recommended export formats.
                            </AlertDescription>
                          </Box>
                      </Alert>
                    </CardBody>
                  </Card>


                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertDescription>
                        VisIt and ParaView are independent third-party visualization tools. SRS is neither affiliated with nor endorsed by Lawrence Livermore National Laboratory (LLNL) or Kitware, the respective developers of VisIt and ParaView. PyFR is an open-source project released under a 3-Clause BSD License; see <Link href="https://www.pyfr.org/" isExternal color="brand.500" fontWeight="bold">pyfr.org</Link> for details.
                      </AlertDescription>
                    </Box>
                  </Alert>



                </VStack>
              </Box>

              <Divider />

              {/* Section 7: Best Practices */}
              <Box id="best-practices">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaLightbulb} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">7. Best Practices & Tips</Heading>
                  </HStack>

                  <Text fontSize="lg" color="gray.700" lineHeight="tall">
                    Get the most out of SRS with these expert tips:
                  </Text>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Card>
                      <CardBody>
                        <Icon as={FaRocket} color="purple.500" boxSize={6} mb={3} />
                        <Heading size="sm" mb={2}>Optimize Your Input Files</Heading>
                        <Text fontSize="sm" color="gray.600">
                          Ensure your simulation parameters are well-defined to avoid wasted compute time. Start with smaller test cases before scaling up.
                        </Text>
                      </CardBody>
                    </Card>

                    <Card>
                      <CardBody>
                        <Icon as={FaCog} color="orange.500" boxSize={6} mb={3} />
                        <Heading size="sm" mb={2}>Use Descriptive Names</Heading>
                        <Text fontSize="sm" color="gray.600">
                          Name your jobs clearly (e.g., "shock_tube_high_res_v3") so you can easily track multiple simulations.
                        </Text>
                      </CardBody>
                    </Card>

                    <Card>
                      <CardBody>
                        <Icon as={FaChartLine} color="green.500" boxSize={6} mb={3} />
                        <Heading size="sm" mb={2}>Monitor Progress Regularly</Heading>
                        <Text fontSize="sm" color="gray.600">
                          Check your jobs periodically to catch any issues early. Premium users get AI-assisted error detection.
                        </Text>
                      </CardBody>
                    </Card>

                    <Card>
                      <CardBody>
                        <Icon as={FaUpload} color="blue.500" boxSize={6} mb={3} />
                        <Heading size="sm" mb={2}>Manage Your Storage</Heading>
                        <Text fontSize="sm" color="gray.600">
                          While storage is unlimited, keep your workspace organized by deleting old jobs you no longer need.
                        </Text>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  <Alert status="success" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Need Help?</AlertTitle>
                      <AlertDescription>
                        Visit our  or contact support through the Settings page. Our team typically responds within 24 hours.
                      </AlertDescription>
                    </Box>
                  </Alert>
                </VStack>
              </Box>

              
              <Divider />

              {/* Section 8: AI Assistance Features */}
              <Box id="ai-features">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaBrain} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">8. AI Assistance Features</Heading>
                  </HStack>

                  <Text fontSize="lg" color="gray.700" lineHeight="tall">
                    SRS goes beyond traditional simulation platforms by integrating advanced AI capabilities to enhance accuracy, catch errors early, and optimize your simulations. Available exclusively for Premium users.
                  </Text>

                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Premium Feature</AlertTitle>
                      <AlertDescription>
                        AI-assisted features are available to Premium users (Standard, Professional, and Enterprise plans). Upgrade to unlock these powerful capabilities.
                      </AlertDescription>
                    </Box>
                  </Alert>

                  {/* AI Features Grid */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Card borderLeft="4px" borderColor="purple.500">
                      <CardBody>
                        <HStack mb={3}>
                          <Icon as={FaCheckCircle} color="purple.500" boxSize={6} />
                          <Heading size="md">Advanced Validation</Heading>
                        </HStack>
                        <Text color="gray.700" mb={3}>
                          Our AI performs comprehensive validation of your pyfr.ini file beyond basic syntax checking:
                        </Text>
                        <List spacing={2}>
                          <ListItem display="flex" alignItems="start">
                            <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                            <Text fontSize="sm"><strong>Syntax Check:</strong> Verifies Python code structure</Text>
                          </ListItem>
                          <ListItem display="flex" alignItems="start">
                            <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                            <Text fontSize="sm"><strong>System Check:</strong> Validates simulation parameters and configurations</Text>
                          </ListItem>
                          <ListItem display="flex" alignItems="start">
                            <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                            <Text fontSize="sm"><strong>Grid Check:</strong> Analyzes mesh quality and resolution</Text>
                          </ListItem>
                          <ListItem display="flex" alignItems="start">
                            <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                            <Text fontSize="sm"><strong>AI Stability Analysis:</strong> Predicts potential numerical instabilities</Text>
                          </ListItem>
                        </List>
                      </CardBody>
                    </Card>

                    <Card borderLeft="4px" borderColor="blue.500">
                      <CardBody>
                        <HStack mb={3}>
                          <Icon as={FaLightbulb} color="blue.500" boxSize={6} />
                          <Heading size="md">Intelligent Recommendations</Heading>
                        </HStack>
                        <Text color="gray.700" mb={3}>
                          Get AI-powered suggestions to optimize your simulations:
                        </Text>
                        <List spacing={2}>
                          <ListItem display="flex" alignItems="start">
                            <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                            <Text fontSize="sm"><strong>Parameter Optimization:</strong> Suggests optimal time steps, convergence criteria</Text>
                          </ListItem>
                          <ListItem display="flex" alignItems="start">
                            <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                            <Text fontSize="sm"><strong>Resource Allocation:</strong> Recommends compute resources based on problem size</Text>
                          </ListItem>
                          <ListItem display="flex" alignItems="start">
                            <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                            <Text fontSize="sm"><strong>Stability Warnings:</strong> Flags potentially unstable configurations</Text>
                          </ListItem>
                        </List>
                      </CardBody>
                    </Card>

                    <Card borderLeft="4px" borderColor="orange.500">
                      <CardBody>
                        <HStack mb={3}>
                          <Icon as={FaChartLine} color="orange.500" boxSize={6} />
                          <Heading size="md">Physics-Aware Validation</Heading>
                        </HStack>
                        <Text color="gray.700" mb={3}>
                          AI validates your simulation setup against physical principles:
                        </Text>
                        <List spacing={2}>
                          <ListItem display="flex" alignItems="start">
                            <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                            <Text fontSize="sm"><strong>CFL Condition Check:</strong> Ensures time step meets stability criteria</Text>
                          </ListItem>
                          <ListItem display="flex" alignItems="start">
                            <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                            <Text fontSize="sm"><strong>Boundary Consistency:</strong> Validates boundary condition compatibility</Text>
                          </ListItem>
                          <ListItem display="flex" alignItems="start">
                            <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                            <Text fontSize="sm"><strong>Material Property Check:</strong> Flags unrealistic physical parameters</Text>
                          </ListItem>
                        </List>
                      </CardBody>
                    </Card>

                    <Card borderLeft="4px" borderColor="green.500">
                      <CardBody>
                        <HStack mb={3}>
                          <Icon as={FaBrain} color="green.500" boxSize={6} />
                          <Heading size="md">AI-Assisted Debugging</Heading>
                        </HStack>
                        <Text color="gray.700" mb={3}>
                          When simulations fail, AI helps you understand why and how to fix it:
                        </Text>
                        <List spacing={2}>
                          <ListItem display="flex" alignItems="start">
                            <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                            <Text fontSize="sm"><strong>Error Analysis:</strong> Interprets error messages in plain language</Text>
                          </ListItem>
                          <ListItem display="flex" alignItems="start">
                            <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                            <Text fontSize="sm"><strong>Root Cause Detection:</strong> Identifies underlying issues</Text>
                          </ListItem>
                          <ListItem display="flex" alignItems="start">
                            <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                            <Text fontSize="sm"><strong>Solution Suggestions:</strong> Provides actionable fixes</Text>
                          </ListItem>
                        </List>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  {/* How to Access AI Features */}
                  <Card bg="brand.50">
                    <CardBody>
                      <Heading size="md" mb={4} color="gray.800">How to Access AI Features</Heading>
                      <OrderedList spacing={3} fontSize="md">
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Upgrade to Premium</Text>
                          <Text color="gray.600" fontSize="sm">
                            Purchase any credit plan (Standard, Professional, or Enterprise) to unlock AI features.
                          </Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Validate with AI</Text>
                          <Text color="gray.600" fontSize="sm">
                            When starting a simulation, click "Validate pyfr.ini file" to trigger comprehensive AI analysis.
                          </Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Review AI Insights</Text>
                          <Text color="gray.600" fontSize="sm">
                            Check the validation report for warnings, recommendations, and stability predictions.
                          </Text>
                        </ListItem>
                        <ListItem>
                          <Text fontWeight="semibold" mb={1}>Monitor Runtime Errors</Text>
                          <Text color="gray.600" fontSize="sm">
                            AI continuously monitors your jobs and provides a comprehensive diagnosis of the error. It provides the error type, root cause, and the corrected code itself.
                          </Text>
                        </ListItem>
                      </OrderedList>
                    </CardBody>
                  </Card>

                  {/* Screenshot Placeholder */}
                  <Box 
                    border="2px dashed" 
                    borderColor="gray.300" 
                    borderRadius="md" 
                    p={8} 
                    bg="gray.50"
                    textAlign="center"
                  >
                    <Image src={valAIRecommendationsScreenshot} alt="AI Validation Recommendations" />
                  </Box>


                  <Box 
                    border="2px dashed" 
                    borderColor="gray.300" 
                    borderRadius="md" 
                    p={8} 
                    bg="gray.50"
                    textAlign="center"
                  >
                    <Image src={autoFixScreenshot} alt="AI Auto-Fix" />
                  </Box>



                  {/* Real-World Benefits */}
                  <Card bg="purple.50">
                    <CardBody>
                      <Heading size="md" mb={4} color="purple.800">Real-World Benefits</Heading>
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                        <VStack align="start" spacing={2}>
                          <Badge colorScheme="purple" fontSize="md" px={3} py={1}>Time Savings</Badge>
                          <Text fontSize="sm" color="gray.700">
                            Catch errors before running simulations, saving hours of wasted compute time
                          </Text>
                        </VStack>
                        <VStack align="start" spacing={2}>
                          <Badge colorScheme="blue" fontSize="md" px={3} py={1}>Higher Accuracy</Badge>
                          <Text fontSize="sm" color="gray.700">
                            AI optimization ensures more stable and accurate simulation results
                          </Text>
                        </VStack>
                        <VStack align="start" spacing={2}>
                          <Badge colorScheme="green" fontSize="md" px={3} py={1}>Faster Learning</Badge>
                          <Text fontSize="sm" color="gray.700">
                            AI explanations help you understand and improve your simulation skills
                          </Text>
                        </VStack>
                      </SimpleGrid>
                    </CardBody>
                  </Card>

                  <Alert status="success" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Ready to Experience AI-Powered Simulations?</AlertTitle>
                      <AlertDescription>
                        Upgrade to a Premium plan today and let AI accelerate your simulation workflow. Visit the <Link as={RouterLink} to="/buy-credits" color="brand.500" fontWeight="semibold">Buy Credits</Link> page to get started.
                      </AlertDescription>
                    </Box>
                  </Alert>
                </VStack>
              </Box>

              <Divider />

              {/* Call to Action section continues here... */}


              {/* Call to Action */}
              <Box bg="brand.50" p={8} borderRadius="lg" textAlign="center">
                <VStack spacing={4}>
                  <Heading size="lg" color="gray.800">
                    Ready to Get Started?
                  </Heading>
                  <Text color="gray.600" maxW="2xl">
                    Now that you know the basics, it's time to run your first simulation and experience the power of GPU-accelerated computing with AI assistance.
                  </Text>
                  <HStack spacing={4}>
                    {user ? (
                      <Tooltip
                        label={
                          !jobEligibility || !jobEligibility.is_eligible 
                            ? jobEligibility?.reason || "Quota limit reached"
                            : `${jobEligibility.remaining_hours} hours remaining`
                        }
                        hasArrow
                        isDisabled={!(!jobEligibility || !jobEligibility.is_eligible)}
                        placement="top"
                      >
                        <Button
                          colorScheme="brand"
                          size="lg"
                          leftIcon={<FaPlay />}
                          onClick={() => navigate('/start-simulation')}
                          isDisabled={!jobEligibility || !jobEligibility.is_eligible}
                          isLoading={jobEligibilityLoading}
                          loadingText="Checking eligibility..."
                        >
                          Start Simulation
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button
                        as={RouterLink}
                        to="/create-user"
                        colorScheme="brand"
                        size="lg"
                        leftIcon={<FaPlay />}
                      >
                        Create Account
                      </Button>
                    )}
                    <Button
                      as={RouterLink}
                      to="/faq"
                      variant="outline"
                      colorScheme="brand"
                      size="lg"
                    >
                      View FAQ
                    </Button>
                  </HStack>
                </VStack>
              </Box>

            </VStack>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default Tutorial;